import React, { useContext, useState } from 'react';
import DatePicker from 'react-datepicker';
import { endOfMonth, format, startOfMonth, parseISO } from 'date-fns';
import * as ics from 'ics';
import 'react-datepicker/dist/react-datepicker.css';
import svLocale from "date-fns/locale/sv";

import { LangContext } from 'context/LangContext';
import getTitleCaseString from 'utils/getTitleCaseString';
import MatchItem from 'components/match/Item';
import Modal from 'components/utils/Modal';
import FormattedMessage from 'components/utils/FormattedMessage';
import * as style from 'styles/components/match/calendar.module.scss';

const Calendar = ({ filteredTeam, matches, isFullHeight = false }) => {
    const { lang } = useContext(LangContext);

    const [startDate, setStartDate] = useState(new Date());
    const [openModal, setOpenModal] = useState(false);
    const [selectedMatches, setSelectedMatches] = useState([]);

    const homeMatches = matches
        .filter((match) => match.local.title === 'Hammarby')
        // .filter((item) => filteredTeam !== null ? item.team.id === filteredTeam : item);
        .filter((item) => !filteredTeam
            ? item.team.title.se === 'Herr' || item.team.title.se === 'Dam'
            : item.team.id === filteredTeam,
        );

    const awayMatches = matches
        .filter((match) => match.external.title === 'Hammarby')
        // .filter((item) => filteredTeam !== null ? item.teamExternal.id === filteredTeam : item);
        .filter((item) => !filteredTeam
            ? item.teamExternal.title.se === 'Herr' || item.teamExternal.title.se === 'Dam'
            : item.teamExternal.id === filteredTeam,
        );

    const highlightDates = [
        {
            'react-datepicker__day--highlighted-home': homeMatches.map(
                (match) => new Date(match.startEventDate),
            ),
        },
        {
            'react-datepicker__day--highlighted-away': awayMatches.map(
                (match) => new Date(match.startEventDate),
            ),
        },
    ];

    const allMatches = homeMatches.concat(awayMatches);

    const exportMatches = [
        ...homeMatches.filter(
            (item) => new Date(item.startEventDate) >= new Date(),
        ),
        ...awayMatches.filter(
            (item) => new Date(item.startEventDate) >= new Date(),
        ),
    ];

    const minDate = startOfMonth(startDate);
    const maxDate = endOfMonth(startDate);

    const handleDateClick = (date) => {
        const matchesSelected = allMatches.filter((match) => {
            if( new Date(match.startEventDate).toDateString() === new Date(date).toDateString() ) {
                return true;
            }
            return false;
        });

        if(!matchesSelected || matchesSelected.length === 0) return;

        setStartDate(date);
        setSelectedMatches(matchesSelected);
        setOpenModal(date);
    };

    const getFormattedDate = (date) =>
        format(parseISO(date), 'yyyy, MM, dd, HH, mm').split(',').map(Number);

    // eslint-disable-next-line consistent-return
    const handleAddToCalendar = () => {
        const { error, value } = ics.createEvents(
            exportMatches.map((event) => ({
                duration: { hours: 1, minutes: 40 },
                location: event.location,
                productId: '',
                start: getFormattedDate(event.startEventDate),
                title: `${event.local.title} - ${event.external.title} (${event.competition?.title})`,
            })),
        );

        if (error) {
            return error;
        }

        const hiddenElement = document.createElement('a');
        hiddenElement.href = `data:attachment/text,${encodeURI(value)}`;
        hiddenElement.target = '_blank';
        hiddenElement.download = 'my-calendar.ics';
        hiddenElement.click();
    };

    return (
        <div className={`${style.calendar} ${isFullHeight ? 'full-height' : ''}`}>
            <Modal
                className={style.calendar__modal}
                show={openModal}
                onClose={() => setOpenModal(false)}
            >
                {selectedMatches && selectedMatches.length &&
                <div>
                    {selectedMatches.map((match, index) => (
                        <MatchItem match={match} key={index} isHorizontalFormat />
                    ))}
                </div>
                }
            </Modal>
            {selectedMatches && selectedMatches.length > 0 && (
                <div>Valda matcher</div>
            )}
            <DatePicker
                locale={svLocale}
                calendarClassName={style.calendar__datepicker}
                calendarStartDay={1}
                highlightDates={highlightDates}
                dayClassName={(date) =>
                    date >= minDate && date <= maxDate
                        ? style.calendar__datepickerDay
                        : style.calendar__datepickerDayOutsideMonth
                }
                disabledKeyboardNavigation
                inline
                onChange={(date) => handleDateClick(date)}
                onMonthChange={(date) => setStartDate(date)}
                shouldCloseOnSelect={false}
                showDisabledMonthNavigation
                previousMonthButtonLabel="\e915"
                renderCustomHeader={({ monthDate, decreaseMonth, increaseMonth }) => (
                    <div className={style.calendar__header}>
                        <button
                            className={`${style.calendar__monthIcon} ${style.calendar__monthIconPrev}`}
                            onClick={decreaseMonth}
                            type="button"
                        >
                            <i className="ico-chevron" />
                        </button>

                        <div className={style.calendar__currentMonth}>
                            {getTitleCaseString(
                                monthDate.toLocaleDateString(lang.iso, {
                                    month: 'long',
                                    year: 'numeric',
                                }),
                            )}
                        </div>

                        <button
                            className={`${style.calendar__monthIcon} ${style.calendar__monthIconNext}`}
                            onClick={increaseMonth}
                            type="button"
                        >
                            <i className="ico-chevron" />
                        </button>
                    </div>
                )}
            />
            <div className={style.calendar__footer}>
                <div className={style.calendar__legend}>
                    <div
                        className={`${style.calendar__legendIcon} ${style.calendar__legendHomeIcon}`}
                    />
                    <span>
                        <FormattedMessage id="match.home" />
                    </span>
                    <div
                        className={`${style.calendar__legendIcon} ${style.calendar__legendAwayIcon}`}
                    />
                    <span>
                        <FormattedMessage id="match.away" />
                    </span>
                </div>

                <button type="button"
                        className="cta-white-ter"
                        onClick={() => handleAddToCalendar()}
                >
                    <FormattedMessage id="match.addToCalendar" />
                </button>
            </div>
        </div>
    );
};

export default Calendar;
